import { call, put, select, takeLatest } from 'redux-saga/effects';
import { toast } from 'utils/toastAbstraction';
import i18n from 'i18next';
import { get, isEmpty } from 'lodash/fp';

import {
  setSubscriptionRequest,
  getSubscriptionsRequest,
  getSubscriptionPlanIdRequest,
  startTrialRequest,
  activateSubscriptionRequest,
  cancelSubscriptionRequest,
  getPricingPlansV2Request,
  buildSubscriptionPlansCatalogQuery,
  parseSubscriptionPlansV2Response,
} from 'services/subscriptions';
import {
  getAllSubscriptions,
  GetAllSubscriptionsPayload,
  getSubscriptionPlanId,
  getCurrentUserSubscription,
  setSubscription,
  paymentApproved,
  startTrial,
  activateSubscription,
  cancelSubscription,
  hasAdblocker,
} from 'store/actionCreators/subscriptions';
import { doCheckIsAuthorized } from 'store/actionCreators/auth';
import { getLocation } from 'services/geolocation';
import { getAuthUser } from 'store/selectors/auth';
import {
  selectAllSubscriptions,
  selectAllOnboardingPlans,
  selectCurrentUserSubscription,
  selectSubscriptionsFetchedAt,
  selectSubscriptionsGeoKey,
  SUBSCRIPTIONS_CACHE_TTL_MS,
} from 'store/selectors/subscriptions';
import { SubscriptionFrequency } from 'types';
import {
  trackPurchase,
  trackStartTrial,
  trackSubscriptionCancel,
  trackSubscriptionRenew,
} from 'utils/analytics';

const getPlansGeoKey = (
  catalogQs: string,
  presentmentOverride?: string,
): string => `${catalogQs}|${presentmentOverride || ''}`;

export function* onGetSubscriptions({
  payload,
}: ReturnType<typeof getAllSubscriptions>) {
  const options = (payload || {}) as GetAllSubscriptionsPayload;
  const force = Boolean(options.force);
  let location: Awaited<ReturnType<typeof getLocation>> | null = null;
  try {
    location = yield call(getLocation);
  } catch (_) {
    location = { country_name: 'Switzerland', country_code2: 'CH' };
    yield put(hasAdblocker.success());
  }

  try {
    const catalogQs = buildSubscriptionPlansCatalogQuery(location);
    const presentmentOverride =
      process.env.REACT_APP_SUBSCRIPTION_PLANS_CURRENCY?.trim().toUpperCase();
    const geoKey = getPlansGeoKey(catalogQs, presentmentOverride);
    const cachedPlans = yield select(selectAllSubscriptions);
    const cachedOnboarding = yield select(selectAllOnboardingPlans);
    const fetchedAt = yield select(selectSubscriptionsFetchedAt);
    const cachedGeoKey = yield select(selectSubscriptionsGeoKey);
    const hasCachedPlans = Boolean(cachedPlans?.length);
    const isStale =
      !fetchedAt || Date.now() - fetchedAt > SUBSCRIPTIONS_CACHE_TTL_MS;
    const isGeoChanged = cachedGeoKey !== geoKey;
    const shouldFetch = force || !hasCachedPlans || isStale || isGeoChanged;

    if (!shouldFetch) {
      yield put(
        getAllSubscriptions.success({
          data: cachedPlans,
          onboarding: cachedOnboarding,
          fetchedAt,
          geoKey: cachedGeoKey,
        }),
      );
      return;
    }

    yield put(getAllSubscriptions.request());
    const body = yield call(getPricingPlansV2Request, catalogQs);
    const { data, onboarding } = parseSubscriptionPlansV2Response(body);
    yield put(getCurrentUserSubscription.trigger());
    yield put(
      getAllSubscriptions.success({
        data,
        onboarding,
        fetchedAt: Date.now(),
        geoKey,
      }),
    );
  } catch (error) {
    const response = get(['response'], error);
    yield put(getAllSubscriptions.failure(response || error));
    if (get(['status'], response) === 401)
      yield put(doCheckIsAuthorized.trigger({}));
  }
}

export function* onGetSubscriptionPlanId({
  payload,
}: ReturnType<typeof getSubscriptionPlanId>) {
  let countryName = '';
  try {
    const { country_name } = yield call(getLocation);
    countryName = country_name;
  } catch (_) {
    countryName = 'Switzerland';
  }
  try {
    yield put(getSubscriptionPlanId.request());
    const { paypalPlanId } = yield call(
      getSubscriptionPlanIdRequest,
      `${
        countryName
          ? `?productId=${payload.productId}&frequency=${
              payload.frequency
            }&country=${countryName.split(' ').join('_').toLowerCase()}`
          : ''
      }`,
    );
    yield put(getSubscriptionPlanId.success(paypalPlanId));
  } catch ({ response }) {
    yield put(getSubscriptionPlanId.failure(response));
    if (get(['status'], response) === 401)
      yield put(doCheckIsAuthorized.trigger({}));
  }
}

export function* onGetCurrentUserSubscription() {
  try {
    yield put(getCurrentUserSubscription.request());
    const data = yield call(getSubscriptionsRequest);
    yield put(getCurrentUserSubscription.success(data));
  } catch ({ response }) {
    if (response.status === 404) {
      yield put(getCurrentUserSubscription.success());
    } else {
      yield put(getCurrentUserSubscription.failure(response.data));
      if (get(['status'], response) === 401)
        yield put(doCheckIsAuthorized.trigger({}));
    }
  }
}

export function* onSetSubscription({
  payload,
}: ReturnType<typeof setSubscription>) {
  let countryName = '';
  try {
    const { country_name } = yield call(getLocation);
    countryName = country_name;
  } catch (_) {
    countryName = 'Switzerland';
  }
  try {
    if (isEmpty(payload)) {
      yield put(setSubscription.success());
    } else {
      const data = {
        ...payload,
        country: countryName.toLowerCase().split(' ').join('_'),
      };
      yield put(setSubscription.request(data));
      const { paypalPlan } = yield call(setSubscriptionRequest, data);
      yield put(setSubscription.success({ paypalPlan }));
    }
  } catch ({ response }) {
    yield toast(i18n.t('subscriptions.failedToSubscribe'), {
      appearance: 'error',
      autoDismiss: true,
    });
    yield put(setSubscription.failure(response));
    if (get(['status'], response) === 401)
      yield put(doCheckIsAuthorized.trigger({}));
  }
}

export function* onPaymentApproved({
  payload,
}: ReturnType<typeof paymentApproved>) {
  try {
    yield put(setSubscription.request(payload));
    const subscription = yield payload.subscription.get();
    yield put(setSubscription.success(subscription));

    // Fire GTM purchase event after PayPal confirms the subscription.
    // The PayPal subscriptionId (from subscription.id) is the unique transaction_id.
    const userProfile = yield select(getAuthUser);
    if (userProfile?.email) {
      yield call(trackPurchase, {
        email: userProfile.email,
        transactionId: subscription?.id || '',
        value: payload.value,
        currency: payload.currency,
        planName: payload.planName,
        billingInterval: payload.billingInterval,
        memberCount: payload.memberCount,
      });
    }

    yield toast(i18n.t('subscriptions.approved'), {
      appearance: 'success',
      autoDismiss: false,
    });
  } catch (e) {
    yield toast(i18n.t('subscriptions.paymentError'), {
      appearance: 'error',
      autoDismiss: true,
    });
    yield put(setSubscription.failure(e));
  }
}

export function* onStartTrial() {
  try {
    const response = yield call(startTrialRequest);
    yield put(activateSubscription.success(response.data));
    const userProfile = yield select(getAuthUser);

    if (userProfile?.email) {
      const planName =
        response.data?.subscriptionPlan?.productName ||
        response.data?.planId ||
        'Trial';

      yield call(trackStartTrial, {
        email: userProfile.email,
        planName,
      });
    }
    yield toast(
      {
        title: i18n.t('notifications.trial_started.title'),
        message: i18n.t('notifications.trial_started.body'),
      },
      {
        appearance: 'success',
        autoDismiss: true,
      },
    );
  } catch (e) {
    let trialErrorKey = 'errors.trial.server_error';
    const status = get(['response', 'status'], e);
    const code = String(
      get(['response', 'data', 'error'], e) || '',
    ).toUpperCase();

    if (status === 409 || code.includes('ALREADY_ACTIVE')) {
      trialErrorKey = 'errors.trial.already_active';
    } else if (status === 403 || code.includes('NOT_ELIGIBLE')) {
      trialErrorKey = 'errors.trial.not_eligible';
    } else if (!status) {
      trialErrorKey = 'errors.trial.network_error';
    }

    yield put(startTrial.failure(trialErrorKey));
    yield put(activateSubscription.failure());
  }
}

export function* onActivateSubscription({
  payload,
}: ReturnType<typeof activateSubscription>) {
  try {
    const response = yield call(activateSubscriptionRequest, payload);
    yield put(activateSubscription.success(response.data));

    const sub = response.data;
    const userProfile = yield select(getAuthUser);
    if (userProfile?.email && sub) {
      const billingInterval =
        sub.frequency === SubscriptionFrequency.YEAR ? 'yearly' : 'monthly';
      const value =
        billingInterval === 'yearly'
          ? sub.subscriptionPlan?.yearlyValue ?? 0
          : sub.subscriptionPlan?.monthlyValue ?? 0;
      const planName =
        sub.subscriptionPlan?.productName ||
        sub.subscriptionPlan?.name ||
        'Unknown';
      const baseId = sub.subscriptionId || sub.id || payload;
      const transactionId = `${baseId}-${Date.now()}`;

      yield call(trackSubscriptionRenew, {
        email: userProfile.email,
        transactionId,
        value,
        currency: sub.currency || sub.subscriptionPlan?.currency || 'CHF',
        planName,
        billingInterval,
      });
    }

    yield toast(i18n.t('subscriptions.activated'), {
      appearance: 'success',
      autoDismiss: false,
    });
  } catch (e) {
    yield toast(e.message, {
      appearance: 'error',
      autoDismiss: true,
    });
    yield put(activateSubscription.failure());
  }
}

export function* onCancelSubscription({
  payload,
}: ReturnType<typeof cancelSubscription>) {
  const subscription = yield select(selectCurrentUserSubscription);

  try {
    const response = yield call(cancelSubscriptionRequest, payload);
    yield put(cancelSubscription.success(response.data));

    if (subscription) {
      const planName =
        subscription.subscriptionPlan?.productName ||
        subscription.subscriptionPlan?.name ||
        'Unknown';
      const billingInterval =
        subscription.frequency === SubscriptionFrequency.YEAR
          ? 'yearly'
          : 'monthly';

      yield call(trackSubscriptionCancel, {
        planName,
        billingInterval,
      });
    }

    yield toast(i18n.t('subscriptions.cancelled'), {
      appearance: 'success',
      autoDismiss: false,
    });
  } catch (e) {
    yield toast(e.message, {
      appearance: 'error',
      autoDismiss: true,
    });
    yield put(cancelSubscription.failure());
  }
}

export default function* () {
  yield takeLatest(getAllSubscriptions, onGetSubscriptions);
  yield takeLatest(getSubscriptionPlanId, onGetSubscriptionPlanId);
  yield takeLatest(getCurrentUserSubscription, onGetCurrentUserSubscription);
  yield takeLatest(setSubscription, onSetSubscription);
  yield takeLatest(paymentApproved, onPaymentApproved);
  yield takeLatest(startTrial, onStartTrial);
  yield takeLatest(activateSubscription, onActivateSubscription);
  yield takeLatest(cancelSubscription, onCancelSubscription);
}
